export const AIStrength = {
  Low: 'low',
  Medium: 'medium',
  High: 'high',
} as const;
export type TAIStrength = (typeof AIStrength)[keyof typeof AIStrength];
export const AIStyle = {
  Balanced: 'balanced',
  Attack: 'attack',
  Cautious: 'cautious',
} as const;
export type TAIStyle = (typeof AIStyle)[keyof typeof AIStyle];
export interface IAISeatProfile {
  readonly strength: TAIStrength;
  readonly style: TAIStyle;
  readonly seed: number;
}
export interface ITableAIProfile {
  readonly tableId: string;
  readonly configVersion: 1;
  readonly strategyVersion: 1;
  readonly seats: readonly [IAISeatProfile, IAISeatProfile, IAISeatProfile, IAISeatProfile];
}

/** 與洗牌完全獨立；不可由 wallSeed 衍生。 */
export const independentAISeed = (): number => crypto.getRandomValues(new Uint32Array(1))[0]!;

export const createTableAIProfile = (
  mixed = false,
  seed: () => number = independentAISeed
): ITableAIProfile => ({
  tableId: mixed ? 'mixed-v1' : 'standard-v1',
  configVersion: 1,
  strategyVersion: 1,
  seats: [
    { strength: AIStrength.Medium, style: AIStyle.Balanced, seed: seed() },
    {
      strength: mixed ? AIStrength.Low : AIStrength.Medium,
      style: AIStyle.Attack,
      seed: seed(),
    },
    { strength: AIStrength.Medium, style: AIStyle.Balanced, seed: seed() },
    {
      strength: mixed ? AIStrength.High : AIStrength.Medium,
      style: AIStyle.Cautious,
      seed: seed(),
    },
  ],
});

export const isTableAIProfile = (value: unknown): value is ITableAIProfile => {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Partial<ITableAIProfile>;
  return (
    typeof p.tableId === 'string' &&
    p.tableId.length > 0 &&
    p.configVersion === 1 &&
    p.strategyVersion === 1 &&
    Array.isArray(p.seats) &&
    p.seats.length === 4 &&
    p.seats.every((s: unknown) => {
      if (typeof s !== 'object' || s === null) return false;
      const seat = s as Partial<IAISeatProfile>;
      return (
        Object.values(AIStrength).includes(seat.strength!) &&
        Object.values(AIStyle).includes(seat.style!) &&
        Number.isInteger(seat.seed) &&
        seat.seed! >= 0 &&
        seat.seed! <= 0xffffffff
      );
    })
  );
};

export const copyTableAIProfile = (value: unknown): ITableAIProfile => {
  if (!isTableAIProfile(value))
    throw new Error('AI 牌桌設定或策略版本不相容，請保留存檔並更新遊戲。');
  return structuredClone(value);
};
